import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, AlertTriangleIcon, AlertCircleIcon, XCircleIcon, ChevronRightIcon, MailIcon, CalendarIcon, FileTextIcon, BookOpenIcon } from 'lucide-react';
type Answer = string | null;
interface Answers {
  q1: Answer;
  q2: Answer;
  q3: Answer;
  q4: Answer;
  q5: Answer;
  q6: Answer;
}
const questions = [{
  id: 'q1' as keyof Answers,
  question: 'How many custom tables have you created?',
  subtitle: '(not out-of-box ServiceNow tables)',
  options: [{
    label: '0-10 (Low complexity)',
    value: 'a',
    points: 5
  }, {
    label: '11-30 (Moderate complexity)',
    value: 'b',
    points: 15
  }, {
    label: '31-75 (High complexity)',
    value: 'c',
    points: 25
  }, {
    label: '76+ (Critical complexity)',
    value: 'd',
    points: 35
  }]
}, {
  id: 'q2' as keyof Answers,
  question: 'How many ServiceNow versions behind are you?',
  subtitle: '',
  options: [{
    label: 'Current version or 1 release behind',
    value: 'a',
    points: 0
  }, {
    label: '2-3 releases behind',
    value: 'b',
    points: 10
  }, {
    label: '4-6 releases behind',
    value: 'c',
    points: 20
  }, {
    label: '7+ releases behind (stuck on old version)',
    value: 'd',
    points: 30
  }]
}, {
  id: 'q3' as keyof Answers,
  question: "What's your latest Instance Scan score?",
  subtitle: '(if you don\'t know, select "Don\'t know")',
  options: [{
    label: "Don't know / Never run Instance Scan",
    value: 'a',
    points: 15
  }, {
    label: '85-100 (Healthy)',
    value: 'b',
    points: 0
  }, {
    label: '70-84 (Moderate risk)',
    value: 'c',
    points: 10
  }, {
    label: '50-69 (High risk)',
    value: 'd',
    points: 20
  }, {
    label: 'Below 50 (Critical risk)',
    value: 'e',
    points: 30
  }]
}, {
  id: 'q4' as keyof Answers,
  question: 'How many point-to-point integrations do you have?',
  subtitle: '',
  options: [{
    label: '0-5 integrations',
    value: 'a',
    points: 0
  }, {
    label: '6-15 integrations',
    value: 'b',
    points: 5
  }, {
    label: '16-30 integrations',
    value: 'c',
    points: 10
  }, {
    label: '31+ integrations',
    value: 'd',
    points: 15
  }]
}, {
  id: 'q5' as keyof Answers,
  question: 'Do you have formal architecture review before development starts?',
  subtitle: '',
  options: [{
    label: 'Yes, all changes reviewed by certified architect',
    value: 'a',
    points: 0
  }, {
    label: 'Sometimes, for major changes only',
    value: 'b',
    points: 5
  }, {
    label: 'No formal review process',
    value: 'c',
    points: 15
  }, {
    label: "What's architecture review?",
    value: 'd',
    points: 20
  }]
}, {
  id: 'q6' as keyof Answers,
  question: 'Does your platform require an ATO?',
  subtitle: '(DoD/IC Bonus Question)',
  options: [{
    label: "Yes, and it's current",
    value: 'a',
    points: 0
  }, {
    label: "Yes, and it's expired or at risk",
    value: 'b',
    points: 10
  }, {
    label: 'No ATO required',
    value: 'c',
    points: 0
  }, {
    label: "Don't know",
    value: 'd',
    points: 0
  }]
}];
function getPoints(qId: keyof Answers, value: string): number {
  const q = questions.find(q => q.id === qId);
  if (!q) return 0;
  const opt = q.options.find(o => o.value === value);
  return opt ? opt.points : 0;
}
function calculateScore(answers: Answers): number {
  let raw = 0;
  for (const key of Object.keys(answers) as (keyof Answers)[]) {
    const val = answers[key];
    if (val) raw += getPoints(key, val);
  }
  return Math.round(raw / 130 * 100);
}
function getRiskLevel(score: number): {
  level: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.ReactNode;
  description: string;
} {
  if (score <= 25) return {
    level: 'Low Risk',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: <CheckCircleIcon className="h-8 w-8 text-green-500" data-id="element-91" />,
    description: 'Your platform is relatively healthy with minimal technical debt. Focus on maintaining your architecture standards and preventing new debt accumulation.'
  };
  if (score <= 50) return {
    level: 'Moderate Risk',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    icon: <AlertCircleIcon className="h-8 w-8 text-yellow-500" data-id="element-92" />,
    description: "You have manageable technical debt but it's growing. Without governance, you'll likely face upgrade challenges within 12-18 months."
  };
  if (score <= 75) return {
    level: 'High Risk',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    icon: <AlertTriangleIcon className="h-8 w-8 text-orange-500" data-id="element-93" />,
    description: "Your platform has significant technical debt that's likely blocking upgrades and creating security/compliance risks. You need immediate governance intervention."
  };
  return {
    level: 'Critical Risk',
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: <XCircleIcon className="h-8 w-8 text-red-500" data-id="element-94" />,
    description: 'Your platform is in severe technical debt. Upgrades are probably impossible without major refactoring. ATO may be at risk. This requires urgent attention.'
  };
}
function getRecommendations(answers: Answers): string[] {
  const recs: string[] = [];
  const q1 = answers.q1;
  const q2 = answers.q2;
  const q3 = answers.q3;
  const q5 = answers.q5;
  if (q1 === 'c' || q1 === 'd') {
    recs.push('Your high number of custom tables suggests heavy customization is blocking upgrades. Start with a technical debt audit and upgrade roadmap to understand the scope of remediation needed.');
  }
  if (q2 === 'c' || q2 === 'd') {
    recs.push('Being 4+ releases behind is a critical signal. Without an upgrade roadmap and architecture standards, this gap will continue to grow. Prioritize upgrade unblocking as your first governance initiative.');
  }
  if (q3 === 'a') {
    recs.push('No Instance Scan history means you have zero visibility into your technical health. Run Instance Scan immediately and review findings—this is the fastest way to quantify your technical debt.');
  } else if (q3 === 'd' || q3 === 'e') {
    recs.push('Your low Instance Scan score indicates significant technical debt. Focus on remediating high-severity findings before they compound into upgrade blockers or security vulnerabilities.');
  }
  if (q5 === 'c' || q5 === 'd') {
    recs.push('Lack of architecture review is allowing new technical debt to accumulate with every development sprint. Implement a governance framework immediately to stop the bleeding before addressing existing debt.');
  }
  if (answers.q6 === 'b') {
    recs.push('An expired or at-risk ATO is an urgent issue. Every undocumented change increases re-authorization risk. Establish ATO governance and document all platform changes immediately.');
  }
  if (answers.q4 === 'c' || answers.q4 === 'd') {
    recs.push('Your integration sprawl (16+ integrations) without governance standards creates fragility. A data governance framework with integration patterns will reduce failure risk and maintenance burden.');
  }
  if (recs.length === 0) {
    recs.push('Your platform shows relatively low technical debt. Focus on maintaining architecture standards and establishing a governance framework to prevent future accumulation.');
    recs.push('Consider a Fractional Platform Owner engagement to provide ongoing governance oversight and quarterly maturity assessments.');
    recs.push('Run a proactive application rationalization assessment to ensure your ServiceNow portfolio is optimized for maximum ROI.');
  }
  return recs.slice(0, 3);
}
export function CalculatorPage() {
  const [answers, setAnswers] = useState<Answers>({
    q1: null,
    q2: null,
    q3: null,
    q4: null,
    q5: null,
    q6: null
  });
  const [showResults, setShowResults] = useState(false);
  const [email, setEmail] = useState('');
  const [sendReport, setSendReport] = useState(false);
  const [subscribe, setSubscribe] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const answeredCount = Object.values(answers).filter(Boolean).length;
  const allAnswered = answeredCount === 6;
  const score = showResults ? calculateScore(answers) : 0;
  const risk = showResults ? getRiskLevel(score) : null;
  const recommendations = showResults ? getRecommendations(answers) : [];
  const handleCalculate = () => {
    if (allAnswered) setShowResults(true);
  };
  const handleReset = () => {
    setAnswers({
      q1: null,
      q2: null,
      q3: null,
      q4: null,
      q5: null,
      q6: null
    });
    setShowResults(false);
    setEmail('');
    setSendReport(false);
    setSubscribe(false);
    setSubmitted(false);
  };
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  return <div className="w-full" data-id="element-95">
      {/* HERO */}
      <section className="bg-nrs-navy py-14 px-4" data-id="element-96">
        <div className="max-w-3xl mx-auto text-center" data-id="element-97">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-98">
            <div className="inline-block px-3 py-1 bg-nrs-orange/20 border border-nrs-orange/40 rounded-full text-nrs-orange text-xs font-semibold uppercase tracking-wider mb-4" data-id="element-99">
              Free Self-Assessment Tool
            </div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-white mb-3" data-id="element-100">
              Is Your ServiceNow Platform at Risk?
            </h1>
            <p className="text-blue-200 text-lg" data-id="element-101">
              Answer 6 quick questions to get your Technical Debt Risk Score and
              personalized recommendations.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-gray-50 py-12 px-4 min-h-screen" data-id="element-102">
        <div className="max-w-2xl mx-auto" data-id="element-103">
          <AnimatePresence mode="wait" data-id="element-104">
            {!showResults ? <motion.div key="questions" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} data-id="element-105">
                {/* Progress Bar */}
                <div className="mb-8" data-id="element-106">
                  <div className="flex items-center justify-between mb-2" data-id="element-107">
                    <span className="text-sm font-semibold text-gray-600" data-id="element-108">
                      {answeredCount} of 6 questions answered
                    </span>
                    <span className="text-sm text-gray-400" data-id="element-109">
                      {Math.round(answeredCount / 6 * 100)}% complete
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-110">
                    <motion.div className="h-full bg-nrs-orange rounded-full" animate={{
                  width: `${answeredCount / 6 * 100}%`
                }} transition={{
                  duration: 0.3
                }} data-id="element-111" />
                  </div>
                </div>

                {/* Questions */}
                <div className="space-y-6" data-id="element-112">
                  {questions.map((q, qi) => <motion.div key={q.id} initial={{
                opacity: 0,
                y: 16
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: qi * 0.05
              }} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-113">
                      <div className="px-6 py-4 border-b border-gray-100 bg-nrs-light" data-id="element-114">
                        <div className="flex items-start gap-3" data-id="element-115">
                          <span className="flex-shrink-0 w-7 h-7 bg-nrs-blue rounded-full flex items-center justify-center text-white text-xs font-bold" data-id="element-116">
                            {qi + 1}
                          </span>
                          <div data-id="element-117">
                            <p className="font-heading font-semibold text-nrs-navy text-sm md:text-base" data-id="element-118">
                              {q.question}
                            </p>
                            {q.subtitle && <p className="text-gray-500 text-xs mt-0.5" data-id="element-119">
                                {q.subtitle}
                              </p>}
                          </div>
                        </div>
                      </div>
                      <div className="px-6 py-4 space-y-2" data-id="element-120">
                        {q.options.map(opt => {
                    const isSelected = answers[q.id] === opt.value;
                    return <button key={opt.value} onClick={() => setAnswers(prev => ({
                      ...prev,
                      [q.id]: opt.value
                    }))} className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all ${isSelected ? 'border-nrs-blue bg-nrs-light text-nrs-navy font-semibold' : 'border-gray-200 bg-white text-gray-700 hover:border-nrs-blue/50 hover:bg-gray-50'}`} data-id="element-121">
                              <div className="flex items-center gap-3" data-id="element-122">
                                <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${isSelected ? 'border-nrs-blue bg-nrs-blue' : 'border-gray-300'}`} data-id="element-123">
                                  {isSelected && <div className="w-full h-full rounded-full bg-white scale-50" data-id="element-124" />}
                                </div>
                                {opt.label}
                              </div>
                            </button>;
                  })}
                      </div>
                    </motion.div>)}
                </div>

                {/* Calculate Button */}
                <div className="mt-8" data-id="element-125">
                  <button onClick={handleCalculate} disabled={!allAnswered} className={`w-full py-4 rounded-xl font-heading font-bold text-lg transition-all ${allAnswered ? 'bg-nrs-orange text-white hover:bg-amber-500 shadow-lg' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-126">
                    {allAnswered ? 'Calculate My Risk Score →' : `Answer all questions to continue (${6 - answeredCount} remaining)`}
                  </button>
                </div>
              </motion.div> : <motion.div key="results" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-127">
                {/* Score Display */}
                <div className={`rounded-2xl border-2 ${risk!.borderColor} ${risk!.bgColor} p-8 text-center mb-6`} data-id="element-128">
                  <div className="flex justify-center mb-4" data-id="element-129">{risk!.icon}</div>
                  <div className="text-gray-500 text-sm mb-1" data-id="element-130">
                    Your Technical Debt Risk Score
                  </div>
                  <div className="font-heading font-extrabold text-6xl text-nrs-navy mb-2" data-id="element-131">
                    {score}
                  </div>
                  <div className="text-gray-400 text-sm mb-3" data-id="element-132">out of 100</div>
                  <div className={`inline-block px-4 py-1.5 rounded-full font-heading font-bold text-sm ${risk!.color} ${risk!.bgColor} border ${risk!.borderColor}`} data-id="element-133">
                    {risk!.level}
                  </div>
                  <p className={`mt-4 text-sm leading-relaxed ${risk!.color} max-w-lg mx-auto`} data-id="element-134">
                    {risk!.description}
                  </p>
                </div>

                {/* Recommendations */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-id="element-135">
                  <h2 className="font-heading font-bold text-lg text-nrs-navy mb-4" data-id="element-136">
                    Your Personalized Recommendations
                  </h2>
                  <div className="space-y-4" data-id="element-137">
                    {recommendations.map((rec, i) => <div key={i} className="flex gap-3" data-id="element-138">
                        <div className="flex-shrink-0 w-6 h-6 bg-nrs-orange rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5" data-id="element-139">
                          {i + 1}
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed" data-id="element-140">
                          {rec}
                        </p>
                      </div>)}
                  </div>
                </div>

                {/* Email Capture */}
                {!submitted ? <div className="bg-nrs-light rounded-xl border border-nrs-blue/20 p-6 mb-6" data-id="element-141">
                    <h3 className="font-heading font-bold text-base text-nrs-navy mb-1" data-id="element-142">
                      Get Your Detailed Report
                    </h3>
                    <p className="text-gray-600 text-sm mb-4" data-id="element-143">
                      Enter your email to receive a detailed breakdown of your
                      risk score and remediation roadmap.
                    </p>
                    <form onSubmit={handleEmailSubmit} className="space-y-3" data-id="element-144">
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your.email@agency.gov" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nrs-blue" required data-id="element-145" />
                      <div className="space-y-2" data-id="element-146">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-id="element-147">
                          <input type="checkbox" checked={sendReport} onChange={e => setSendReport(e.target.checked)} className="rounded" data-id="element-148" />
                          Send me my detailed risk score report
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-id="element-149">
                          <input type="checkbox" checked={subscribe} onChange={e => setSubscribe(e.target.checked)} className="rounded" data-id="element-150" />
                          Subscribe to ServiceNow Governance Insights newsletter
                        </label>
                      </div>
                      <button type="submit" className="w-full py-2.5 bg-nrs-blue text-white font-semibold text-sm rounded-lg hover:bg-blue-800 transition-colors" data-id="element-151">
                        Send My Report
                      </button>
                    </form>
                  </div> : <div className="bg-green-50 rounded-xl border border-green-200 p-6 mb-6 text-center" data-id="element-152">
                    <CheckCircleIcon className="h-8 w-8 text-green-500 mx-auto mb-2" data-id="element-153" />
                    <p className="font-semibold text-green-800" data-id="element-154">
                      Report sent! Check your inbox shortly.
                    </p>
                  </div>}

                {/* Next Steps */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-id="element-155">
                  <h2 className="font-heading font-bold text-lg text-nrs-navy mb-4" data-id="element-156">
                    Next Steps
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-157">
                    <a href="mailto:info@newriversystems.com" className="border border-nrs-blue/20 rounded-lg p-4 hover:border-nrs-blue hover:shadow-sm transition-all text-center" data-id="element-158">
                      <CalendarIcon className="h-6 w-6 text-nrs-blue mx-auto mb-2" data-id="element-159" />
                      <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-160">
                        Free Consultation
                      </div>
                      <p className="text-gray-500 text-xs" data-id="element-161">
                        Discuss your results with a ServiceNow governance expert
                      </p>
                    </a>
                    <a href="#" className="border border-nrs-blue/20 rounded-lg p-4 hover:border-nrs-blue hover:shadow-sm transition-all text-center" data-id="element-162">
                      <FileTextIcon className="h-6 w-6 text-nrs-blue mx-auto mb-2" data-id="element-163" />
                      <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-164">
                        Download Report
                      </div>
                      <p className="text-gray-500 text-xs" data-id="element-165">
                        "The Federal ServiceNow Technical Debt Audit Guide"
                        (PDF)
                      </p>
                    </a>
                    <Link to="/case-studies" className="border border-nrs-blue/20 rounded-lg p-4 hover:border-nrs-blue hover:shadow-sm transition-all text-center" data-id="element-166">
                      <BookOpenIcon className="h-6 w-6 text-nrs-blue mx-auto mb-2" data-id="element-167" />
                      <div className="font-heading font-semibold text-sm text-nrs-navy mb-1" data-id="element-168">
                        Read Case Study
                      </div>
                      <p className="text-gray-500 text-xs" data-id="element-169">
                        How an agency reduced technical debt from Critical to
                        Low in 6 months
                      </p>
                    </Link>
                  </div>
                </div>

                <button onClick={handleReset} className="w-full py-3 border-2 border-gray-300 text-gray-600 font-semibold rounded-xl hover:border-nrs-blue hover:text-nrs-blue transition-colors" data-id="element-170">
                  Retake Assessment
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </section>
    </div>;
}